// Push notification service for MoZo.Live
import { supabase } from './supabase';
import { notificationService } from './notification-service';

export interface PushNotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  image?: string;
  data?: any;
  actions?: NotificationAction[];
  tag?: string;
}

export interface NotificationAction {
  action: string;
  title: string;
  icon?: string;
}

class PushNotificationService {
  private registration: ServiceWorkerRegistration | null = null;
  private permission: NotificationPermission = 'default';

  constructor() {
    this.checkPermission();
    this.registerServiceWorker();
  }

  // Check current notification permission
  private checkPermission(): void {
    if ('Notification' in window) {
      this.permission = Notification.permission;
    }
  }

  // Register service worker for push notifications
  private async registerServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        this.registration = await navigator.serviceWorker.register('/sw.js');
        console.log('Service Worker registered:', this.registration);
      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    }
  }

  // Request notification permission
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return 'denied';
    }

    if (this.permission === 'default') {
      this.permission = await Notification.requestPermission();
    }

    return this.permission;
  }

  // Subscribe to push notifications
  async subscribeToPush(): Promise<PushSubscription | null> {
    if (!this.registration) {
      console.error('Service Worker not registered');
      return null;
    }

    try {
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY
      });

      // Save subscription to Supabase
      await this.saveSubscription(subscription);
      return subscription;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return null;
    }
  }

  // Save push subscription to database
  private async saveSubscription(subscription: PushSubscription): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      await supabase
        .from('push_subscriptions')
        .upsert({
          user_id: user.id,
          subscription: JSON.stringify(subscription),
          created_at: new Date().toISOString()
        });
    } catch (error) {
      console.error('Failed to save push subscription:', error);
    }
  }

  // Send local notification
  async sendLocalNotification(payload: PushNotificationPayload): Promise<void> {
    if (this.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    try {
      const notification = new Notification(payload.title, {
        body: payload.body,
        icon: payload.icon || '/icons/icon-192x192.png',
        tag: payload.tag
      });

      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        
        if (payload.data?.url) {
          window.location.href = payload.data.url;
        }
        
        notification.close();
      };

    } catch (error) {
      console.error('Failed to send local notification:', error);
    }
  }

  // Send comment notification
  async sendCommentNotification(
    recipientId: string,
    commenterName: string,
    postId: string,
    commentContent: string
  ): Promise<void> {
    // Create database notification
    await notificationService.createNotification(
      recipientId,
      'comment',
      null,
      `${commenterName} commented: ${commentContent.substring(0, 100)}...`,
      'post',
      postId
    );

    // Send push notification
    await this.sendPushToUser(recipientId, {
      title: 'New Comment',
      body: `${commenterName} commented on your post`,
      icon: '/icons/comment-icon.png',
      data: {
        type: 'comment',
        postId,
        url: `/post/${postId}`
      },
      tag: `comment-${postId}`,
      actions: [
        { action: 'view', title: 'View Post', icon: '/icons/view-icon.png' },
        { action: 'reply', title: 'Reply', icon: '/icons/reply-icon.png' }
      ]
    });
  }

  // Send message notification
  async sendMessageNotification(
    recipientId: string,
    senderName: string,
    conversationId: string,
    messageContent: string
  ): Promise<void> {
    // Create database notification
    await notificationService.createNotification(
      recipientId,
      'message',
      null,
      `${senderName}: ${messageContent.substring(0, 100)}...`,
      'conversation',
      conversationId
    );

    // Send push notification
    await this.sendPushToUser(recipientId, {
      title: `Message from ${senderName}`,
      body: messageContent.substring(0, 100),
      icon: '/icons/message-icon.png',
      data: {
        type: 'message',
        conversationId,
        url: `/message?conversation=${conversationId}`
      },
      tag: `message-${conversationId}`,
      actions: [
        { action: 'reply', title: 'Reply', icon: '/icons/reply-icon.png' },
        { action: 'view', title: 'View Chat', icon: '/icons/view-icon.png' }
      ]
    });
  }

  // Send push notification to specific user
  private async sendPushToUser(
    userId: string,
    payload: PushNotificationPayload
  ): Promise<void> {
    try {
      // Get user's push subscriptions
      const { data: subscriptions } = await supabase
        .from('push_subscriptions')
        .select('subscription')
        .eq('user_id', userId);

      if (!subscriptions || subscriptions.length === 0) {
        console.log('No push subscriptions found for user:', userId);
        return;
      }

      // Send to all user's devices
      for (const sub of subscriptions) {
        try {
          const subscription = JSON.parse(sub.subscription);
          await this.sendPushMessage(subscription, payload);
        } catch (error) {
          console.error('Failed to send push to subscription:', error);
        }
      }
    } catch (error) {
      console.error('Failed to send push notification:', error);
    }
  }

  // Send push message to subscription
  private async sendPushMessage(
    _subscription: PushSubscription,
    payload: PushNotificationPayload
  ): Promise<void> {
    // This would typically be done on the server side
    // For now, we'll use the local notification as fallback
    await this.sendLocalNotification(payload);
  }

  // Initialize push notifications for current user
  async initialize(): Promise<void> {
    const permission = await this.requestPermission();
    
    if (permission === 'granted') {
      await this.subscribeToPush();
      console.log('Push notifications initialized');
    } else {
      console.warn('Push notifications not permitted');
    }
  }

  // Check if push notifications are supported
  isSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  }

  // Get current permission status
  getPermission(): NotificationPermission {
    return this.permission;
  }
}

// Create singleton instance
export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
